import * as React from "react";

import { useOpenModal } from "@editor/hooks/useModal";
import { docs } from "@editor/utils/docs";

import { useAnalyticsQueryContext } from "@/features/analytics/contexts/AnalyticsQueryContext";
import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { ArrowUpRight, ChartLine, Rocket, Search } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

export const AnalyticsEmptyState: React.FC<{
  type: "resetFilters" | "noTraffic" | "unlockAnalytics";
}> = ({ type = "noTraffic" }) => {
  return exhaustiveSwitch({ type })({
    resetFilters: <EmptyStateResetFilters />,
    noTraffic: <EmptyStateNoTraffic />,
    unlockAnalytics: <EmptyStateUnlockAnalytics />,
  });
};

const EmptyStateResetFilters: React.FC = () => {
  const { resetInternalAndUrlSyncedQueries } = useAnalyticsQueryContext();

  return (
    <EmptyState
      size="sm"
      variant="noRings"
      title="No matching results"
      description="Please adjust your filters and try again."
      layoutClassName="h-[300px]"
      icon={<Search size={18} />}
    >
      <Button
        size="sm"
        variant="primary"
        onClick={() => {
          resetInternalAndUrlSyncedQueries();
        }}
      >
        Reset filters
      </Button>
    </EmptyState>
  );
};

const EmptyStateNoTraffic: React.FC = () => {
  return (
    <EmptyState
      size="sm"
      variant="pinging"
      title="No traffic yet"
      layoutClassName="h-[300px]"
      description="We’ll populate this chart as soon as your pages start getting new traffic"
    >
      <Button
        size="sm"
        variant="primary"
        endEnhancer={<ArrowUpRight size={16} />}
        onClick={() => {
          window.open(docs.analytics.howToDriveTraffic, "_blank");
        }}
      >
        How to drive traffic
      </Button>
    </EmptyState>
  );
};

const EmptyStateUnlockAnalytics: React.FC = () => {
  const openModal = useOpenModal();

  const openBillingModal = () =>
    openModal({
      type: "billingModal",
      props: {
        source: "analytics",
        billingPlanMessageKey: "billingPlan.analytics",
      },
    });

  return (
    <EmptyState
      size="sm"
      variant="noRings"
      title="See How Your Store is Trending"
      layoutClassName="h-[300px]"
      description="See weekly trends & long-term growth patterns."
      icon={<ChartLine size={18} />}
    >
      <Button
        size="sm"
        variant="primary"
        startEnhancer={<Rocket size={14} />}
        onClick={openBillingModal}
      >
        Unlock
      </Button>
    </EmptyState>
  );
};
